import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

export type ButtonVariant =
  'primary' | 'secondary' | 'ghost' | 'outline' | 'danger' | 'danger-ghost' | 'link'
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm ' +
    'disabled:hover:bg-primary',
  secondary:
    'bg-surface-raised text-foreground border border-border hover:border-border-strong ' +
    'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
  ghost:
    'text-muted-foreground hover:text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
  outline:
    'border border-border text-foreground hover:border-border-strong ' +
    'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
  danger: 'bg-danger text-white hover:opacity-90 shadow-sm',
  'danger-ghost': 'text-danger hover:bg-danger-soft',
  link: 'text-primary-text hover:underline underline-offset-4 px-0',
}

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2 text-[12px] gap-1.5 rounded-md',
  sm: 'h-8 px-2.5 text-[13px] gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-[13px] gap-2 rounded-md',
  lg: 'h-10 px-5 text-[14px] gap-2 rounded-lg',
  icon: 'h-9 w-9 rounded-md',
  'icon-sm': 'h-7 w-7 rounded-md',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: ReactNode
  trailingIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'secondary',
    size = 'md',
    loading,
    icon,
    trailingIcon,
    children,
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // A loading button stays disabled so a double-click cannot fire twice.
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap',
        'transition-colors duration-150 select-none',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="size-3.5 animate-spin" strokeWidth={2} aria-hidden /> : icon}
      {children}
      {!loading && trailingIcon}
    </button>
  )
})
